/**
 * Iter 220g — Registration Certification (Customer Management sub-module)
 *
 * Four tabs:
 *   1. Settings              — template branding, watermark, placeholders
 *   2. Generate Certificate  — customer picker + waste types + validity
 *                              + course-completed toggle → wizard
 *   3. Generated              — list of issued certificates with PDF /
 *                               WhatsApp / Email / Delete actions
 *   4. Requests              — customers who ticked YES on the public
 *                               registration form. One-click "Generate"
 *                               that opens the wizard prefilled with
 *                               that customer.
 *
 * We reuse the battle-tested Wizard / TemplateBrandingModal /
 * SendEmailModal already living in `admin/AdminRegCerts.jsx` so we
 * don't duplicate 900 lines of PDF-facing logic.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Certificate, Gear, Plus, ListChecks, EnvelopeOpen,
  DownloadSimple, Printer, WhatsappLogo, EnvelopeSimple, Trash,
  MagnifyingGlass, ArrowRight, CheckCircle,
} from "@phosphor-icons/react";
import {
  Wizard, TemplateBrandingModal, SendEmailModal, TEMPLATE_ACCENTS,
} from "../admin/AdminRegCerts";

const TABS = [
  { key: "settings",  label: "Settings",              icon: Gear },
  { key: "generate",  label: "Generate Certificate",  icon: Plus },
  { key: "generated", label: "Generated Certificates", icon: ListChecks },
  { key: "requests",  label: "Certificate Requests",  icon: EnvelopeOpen },
];

export default function CustomerRegistrationCerts() {
  const [tab, setTab] = useState("generate");
  // Shared data
  const [customers, setCustomers] = useState([]);
  const [master, setMaster] = useState({ waste_types: [], templates: [] });
  const [collectionAgencies, setCollectionAgencies] = useState([]);
  const [disposalAgencies, setDisposalAgencies] = useState([]);
  const [certs, setCerts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [prefillCustomer, setPrefillCustomer] = useState(null);
  const [emailingCert, setEmailingCert] = useState(null);
  const [q, setQ] = useState("");

  const load = () => Promise.all([
    api.get("/reg-certs").then(r => setCerts(r.data)),
    api.get("/reg-certs/master-data").then(r => setMaster(r.data)),
    api.get("/reg-certs/requests").then(r => setRequests(r.data)),
    api.get("/agencies?kind=collection&active_only=true").then(r => setCollectionAgencies(r.data)).catch(() => {}),
    api.get("/agencies?kind=disposal&active_only=true").then(r => setDisposalAgencies(r.data)).catch(() => {}),
    api.get("/customers?limit=2000").then(r => setCustomers(r.data)).catch(() => {}),
  ]).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const downloadPdf = async (cert) => {
    try {
      const r = await api.get(`/reg-certs/${cert.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${cert.cert_no}.pdf`; a.click();
      URL.revokeObjectURL(url); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const printPdf = async (cert) => {
    try {
      const r = await api.get(`/reg-certs/${cert.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank"); w?.focus();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const shareOnWhatsApp = async (cert) => {
    const phone = (cert.customer_snapshot?.phone || "").replace(/[^0-9]/g, "");
    if (!phone) { toast.error("Customer has no phone on file"); return; }
    const base = (window.location.origin || "").replace(/\/$/, "");
    const verify = `${base}/verify/registration-cert/${cert.cert_no}`;
    const waNumber = phone.length === 10 ? `91${phone}` : phone;
    const msg = [
      `Namaste ${cert.customer_snapshot?.name || ""},`, "",
      `Your Waste Disposal Registration Certificate ${cert.cert_no} is ready.`,
      `Valid until ${cert.valid_until}.`, "",
      `Verify online: ${verify}`, "", "— Rescigo Health",
    ].join("\n");
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    try { await api.post(`/reg-certs/${cert.id}/log-whatsapp`, {}); load(); } catch {}
  };
  const removeCert = async (cert) => {
    if (!window.confirm(`Delete certificate ${cert.cert_no}?`)) return;
    try { await api.delete(`/reg-certs/${cert.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Generate-tab prefill helpers (used from Requests tab)
  const openGenerateFor = (customer) => {
    setPrefillCustomer(customer);
    setTab("generate");
  };

  const filteredRequests = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return requests;
    return requests.filter((r) =>
      (r.name || "").toLowerCase().includes(ql)
      || (r.phone || "").toLowerCase().includes(ql)
      || (r.customer_code || "").toLowerCase().includes(ql));
  }, [q, requests]);

  const filteredCerts = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return certs;
    return certs.filter((c) =>
      (c.customer_snapshot?.name || "").toLowerCase().includes(ql)
      || (c.customer_snapshot?.phone || "").toLowerCase().includes(ql)
      || (c.cert_no || "").toLowerCase().includes(ql));
  }, [q, certs]);

  return (
    <AppShell title="Registration Certification"
              subtitle="Customer Management · Waste Disposal Registration Certificates">
      {/* Tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-1 border-b border-[#E5E2D9]">
        {TABS.map((t) => {
          const active = tab === t.key;
          const count = t.key === "requests"
            ? requests.filter((r) => !r.has_cert).length
            : t.key === "generated" ? certs.length : null;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`regcert-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                      active
                        ? "border-[#DC2626] text-[#DC2626]"
                        : "border-transparent text-[#5C5A55] hover:text-[#1A1A1A]"
                    }`}>
              <t.icon size={14} weight="bold"/>{t.label}
              {count != null && count > 0 && (
                <span className={`ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                  active ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#E5E2D9] text-[#5C5A55]"
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search bar (visible on data tabs) */}
      {(tab === "generated" || tab === "requests") && (
        <div className="mb-4 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="regcert-search"
                 placeholder="Search by name, phone, code or cert no."
                 className="w-full max-w-md h-10 pl-9 pr-3 border border-[#E5E2D9] rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#DC2626]/20"/>
        </div>
      )}

      {tab === "settings" && (
        <div data-testid="regcert-settings-panel">
          <TemplateBrandingModal templates={master.templates} onClose={null} inline />
        </div>
      )}

      {tab === "generate" && (
        <div data-testid="regcert-generate-panel">
          <Wizard
            inline
            prefillCustomer={prefillCustomer}
            onClose={() => setPrefillCustomer(null)}
            customers={customers}
            master={master}
            collectionAgencies={collectionAgencies}
            disposalAgencies={disposalAgencies}
            onCreated={(c) => {
              toast.success(`Certificate ${c.cert_no} generated`);
              setPrefillCustomer(null);
              downloadPdf(c);
              setTab("generated");
              load();
            }}
          />
        </div>
      )}

      {tab === "generated" && (
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto"
             data-testid="regcert-generated-panel">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-6 py-3">Cert no.</th>
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Waste streams</th>
                <th className="px-6 py-3">Template</th>
                <th className="px-6 py-3">Valid until</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCerts.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-[#8A8780]">
                  <Certificate size={30} weight="duotone" className="mx-auto mb-2 text-[#8A8780]"/>
                  No certificates match your search. Try the Generate tab.
                </td></tr>
              )}
              {filteredCerts.map((c, i) => (
                <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}
                    data-testid={`regcert-row-${c.id}`}>
                  <td className="px-6 py-3 font-mono text-xs" style={{ color: TEMPLATE_ACCENTS[c.template_id] || "#0B2B66" }}>
                    {c.cert_no}
                  </td>
                  <td className="px-6 py-3">
                    <div className="font-medium">{c.customer_snapshot?.name}</div>
                    <div className="text-[10px] text-[#8A8780]">
                      {c.customer_snapshot?.phone} · {c.customer_snapshot?.district}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-xs max-w-[240px]">
                    <div className="line-clamp-2">{(c.waste_types || []).join(", ")}</div>
                  </td>
                  <td className="px-6 py-3 text-xs capitalize">{c.template_id?.replace(/_/g, " ")}</td>
                  <td className="px-6 py-3 text-xs">{c.valid_until}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button title="Download PDF" onClick={() => downloadPdf(c)} data-testid={`rc-dl-${c.id}`}
                              className="text-[#2D5A27] text-xs inline-flex items-center gap-1">
                        <DownloadSimple size={12}/>PDF
                      </button>
                      <button title="Print" onClick={() => printPdf(c)} data-testid={`rc-print-${c.id}`}
                              className="text-[#5C5A55] text-xs inline-flex items-center gap-1">
                        <Printer size={12}/>Print
                      </button>
                      <button title="Share on WhatsApp" onClick={() => shareOnWhatsApp(c)} data-testid={`rc-wa-${c.id}`}
                              className="text-[#25D366] text-xs inline-flex items-center gap-1">
                        <WhatsappLogo size={12} weight="fill"/>WhatsApp
                      </button>
                      <button title="Email" onClick={() => setEmailingCert(c)} data-testid={`rc-email-${c.id}`}
                              className="text-[#0B2B66] text-xs inline-flex items-center gap-1">
                        <EnvelopeSimple size={12}/>Email
                      </button>
                      <button title="Delete" onClick={() => removeCert(c)} data-testid={`rc-del-${c.id}`}
                              className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                        <Trash size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "requests" && (
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto"
             data-testid="regcert-requests-panel">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Code</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Requested</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-[#8A8780]">
                  <EnvelopeOpen size={30} weight="duotone" className="mx-auto mb-2 text-[#8A8780]"/>
                  No pending certificate requests. When customers tick
                  "I need a Rescigo registration certificate" on the public
                  registration form they'll show up here.
                </td></tr>
              )}
              {filteredRequests.map((c, i) => (
                <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}
                    data-testid={`regcert-request-row-${c.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium">{c.name || "—"}</div>
                    <div className="text-[10px] text-[#8A8780] max-w-[240px] truncate">{c.address || ""}</div>
                  </td>
                  <td className="px-6 py-3 font-mono text-xs">{c.phone}</td>
                  <td className="px-6 py-3 font-mono text-xs">{c.customer_code || "—"}</td>
                  <td className="px-6 py-3 text-xs capitalize">{c.customer_type || "—"}</td>
                  <td className="px-6 py-3">
                    {c.has_cert ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[#15803D]">
                        <CheckCircle size={11} weight="fill"/>Issued
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#F9E4B7] text-[#7A5A11] rounded">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-xs">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric"
                    }) : "—"}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => openGenerateFor(c)}
                            data-testid={`regcert-generate-${c.id}`}
                            className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-xs font-bold">
                      <Certificate size={12} weight="fill"/>
                      {c.has_cert ? "Re-issue" : "Generate"}
                      <ArrowRight size={10} weight="bold"/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {emailingCert && (
        <SendEmailModal cert={emailingCert} onClose={() => setEmailingCert(null)}
                        onSent={() => { setEmailingCert(null); load(); }}/>
      )}
    </AppShell>
  );
}
